/* Version: 3.5.137 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import { FacebookOutlined, InstagramOutlined, LinkedinOutlined, TwitterOutlined, YoutubeOutlined } from '@manulife/mux-cds-icons';
import { Manulife, ManulifeFr } from '@manulife/mux-logos';
import PropTypes from 'prop-types';
import TextLink from '../TextLink/index.js';
import i18n from '../../lang/i18n/index.js';
import LANG from '../../lang/lang.js';
import { safeRest, rem } from '../../utils/functions.js';
import { SVG_ICON_SIZE_DEFAULT } from '../Icon/constants.js';
import { SOCIAL_MEDIA, DEFAULT_FOOTER_LOGO_STYLE } from './constants.js';
import LinkDropdown from './LinkDropdown.js';
import { FooterWrapper, SiteLinksSection, SiteLinksList, SocialMediaLinksList, LegalSection } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "items", "copyrightText", "copyrightYear", "copyrightYearAria", "logo", "legalText", "quickLinks", "socialMediaLinks"];
var Footer = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
    _ref$items = _ref.items,
    items = _ref$items === undefined ? [] : _ref$items,
    copyrightText = _ref.copyrightText,
    _ref$copyrightYear = _ref.copyrightYear,
    copyrightYear = _ref$copyrightYear === undefined ? "\xA9 1999-".concat(new Date().getFullYear()) : _ref$copyrightYear,
    _ref$copyrightYearAri = _ref.copyrightYearAria,
    copyrightYearAria = _ref$copyrightYearAri === undefined ? "\xA9 1999-".concat(new Date().getFullYear()) : _ref$copyrightYearAri,
    logo = _ref.logo,
    legalText = _ref.legalText,
    quickLinks = _ref.quickLinks,
    _ref$socialMediaLinks = _ref.socialMediaLinks,
    socialMediaLinks = _ref$socialMediaLinks === undefined ? [] : _ref$socialMediaLinks,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('footer-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var safeProps = safeRest(rest);
  var lang = useLang();
  var theme = useTheme();
  var DefaultLogo = lang === LANG.EN_CA ? Manulife : ManulifeFr; // NOSONAR

  var CDSSocialIcons = _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, SOCIAL_MEDIA.FACEBOOK, FacebookOutlined), SOCIAL_MEDIA.INSTAGRAM, InstagramOutlined), SOCIAL_MEDIA.LINKEDIN, LinkedinOutlined), SOCIAL_MEDIA.TWITTER, TwitterOutlined), SOCIAL_MEDIA.YOUTUBE, YoutubeOutlined);
  return /*#__PURE__*/jsxs(FooterWrapper, _objectSpread(_objectSpread({
    id: id,
    theme: theme,
    ref: ref,
    tabIndex: -1
  }, safeProps), {}, {
    children: [(quickLinks === null || quickLinks === undefined ? undefined : quickLinks.length) > 0 && /*#__PURE__*/jsx(LinkDropdown, {
      id: "quick-links",
      title: i18n[lang].footer.quickLinksHeader,
      links: quickLinks
    }), /*#__PURE__*/jsxs(SiteLinksSection, {
      theme: theme,
      children: [(items === null || items === undefined ? undefined : items.length) > 0 && /*#__PURE__*/jsx(SiteLinksList, {
        lang: lang,
        "data-testid": "site-links-list",
        children: items.map(function (item) {
          var textLinkProps;
          if (item.onClick) {
            textLinkProps = {
              as: 'button',
              onClick: item.onClick
            };
          } else {
            textLinkProps = {
              to: item.href
            };
          }
          return /*#__PURE__*/jsx("li", {
            "data-testid": "site-links-item-".concat(item.id),
            children: /*#__PURE__*/jsxs(TextLink, _objectSpread(_objectSpread({
              id: "".concat(id, "-").concat(item.id),
              variant: TextLink.VARIANT.INVERSE,
              isExternal: item.isExternal
            }, textLinkProps), {}, {
              children: [item.title, item.icon && /*#__PURE__*/jsx("span", {
                className: "mux-Footer-icon",
                children: item.icon
              })]
            }))
          }, item.id);
        })
      }), (socialMediaLinks === null || socialMediaLinks === undefined ? undefined : socialMediaLinks.length) > 0 && /*#__PURE__*/jsx(SocialMediaLinksList, {
        "data-testid": "social-links-list",
        theme: theme,
        children: socialMediaLinks.map(function (link) {
          var SocialIcon = CDSSocialIcons[link.site];
          return /*#__PURE__*/jsx("li", {
            "data-testid": "social-links-item-".concat(link.site),
            children: link.onClick ? /*#__PURE__*/jsx("button", {
              type: "button",
              id: "social-button-".concat(link.site),
              onClick: link.onClick,
              "aria-label": "".concat(link.site),
              children: /*#__PURE__*/jsx(SocialIcon, {
                fontSize: rem(SVG_ICON_SIZE_DEFAULT)
              })
            }) : /*#__PURE__*/jsx("a", {
              id: "social-a-".concat(link.site),
              href: link.href,
              target: "_blank",
              rel: "noopener noreferrer",
              "aria-label": "".concat(link.site, ", ").concat(i18n[lang].footer.externalLinkText),
              children: /*#__PURE__*/jsx(SocialIcon, {
                fontSize: rem(SVG_ICON_SIZE_DEFAULT)
              })
            })
          }, "social-".concat(link.site));
        })
      })]
    }), /*#__PURE__*/jsxs(LegalSection, {
      theme: theme,
      "data-haslegal": !!legalText,
      children: [logo || /*#__PURE__*/jsx(DefaultLogo.REVERSE, {
        title: i18n[lang].footer.logoAltText,
        style: DEFAULT_FOOTER_LOGO_STYLE
      }), /*#__PURE__*/jsxs("span", {
        className: "mux-Footer-copyright",
        children: [/*#__PURE__*/jsx("span", {
          "aria-label": copyrightYear === copyrightYearAria ? undefined : copyrightYearAria,
          children: copyrightYear
        }), ' ', /*#__PURE__*/jsx("span", {
          children: copyrightText || i18n[lang].footer.defaultCopyrightText
        })]
      }), legalText && /*#__PURE__*/jsx("span", {
        className: "mux-Footer-legal",
        children: legalText
      })]
    })]
  }));
});
process.env.NODE_ENV !== "production" ? Footer.propTypes = {
  /** ID for footer */
  id: PropTypes.string,
  /** An array of items to display in the links section of the footer */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    href: PropTypes.string,
    icon: PropTypes.node,
    isExternal: PropTypes.bool,
    onClick: PropTypes.func
  })),
  /** Copyright year */
  copyrightYear: PropTypes.string,
  /** Copyright year aria label */
  copyrightYearAria: PropTypes.string,
  /** Copyright text */
  copyrightText: PropTypes.string,
  /** Legal details like disclaimers etc. */
  legalText: PropTypes.string,
  /** Logo component. Recommended to use with `@manulife/mux-logos` package */
  logo: PropTypes.node,
  /** Quick Links options */
  quickLinks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    href: PropTypes.string,
    isExternal: PropTypes.bool,
    onClick: PropTypes.func
  })),
  /** List of social media links to display */
  socialMediaLinks: PropTypes.arrayOf(PropTypes.shape({
    site: PropTypes.oneOf([SOCIAL_MEDIA.FACEBOOK, SOCIAL_MEDIA.INSTAGRAM, SOCIAL_MEDIA.LINKEDIN, SOCIAL_MEDIA.TWITTER, SOCIAL_MEDIA.YOUTUBE]),
    href: PropTypes.string,
    onClick: PropTypes.func
  }))
} : undefined;
process.env.NODE_ENV !== "production" ? Footer.displayName = 'Footer' : undefined;

export { Footer as default };
